import React, { Suspense } from 'react';
import { Switch, useLocation, Route as RotaPura, Redirect } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { CircularProgress, Box } from '@mui/material';
import Route from './Route';
import AlunoRoute from './AlunoRoute';
import EmpresaRoute from './EmpresaRoute';
import AdminRoute from './AdminRoute';
import AdminCertificadosLote from '../pages/AdminCertificadosLote';
import HqRoute from './HqRoute';
import PageTransition from '../components/PageTransition';
import redirecionaAluno from '../pages/WorkModulo/redirecionaAluno';

// ============================================================
// TASK-020: Code Splitting por módulo com React.lazy
// Módulos pesados são carregados sob demanda.
// Páginas leves/críticas (login, landing) são importadas normalmente.
// ============================================================

// --- Páginas leves (carregamento imediato) ---
import Main from '../pages/Main';
import SignIn from '../pages/SignIn';
import ChangePassword from '../pages/ChangePassword';
import SignOut from '../pages/SignOut';
import Confirm from '../pages/Confirm/index';
import ForgotPasword from '../pages/ForgotPassword/index';
import SignUpAluno from '../pages/SignUpAluno';
import SignUpEmpresa from '../pages/SignUpEmpresa';
import SelectsignUp2 from '../pages/SelectSignUp';
import Error from '../pages/Error';
import Help from '../pages/Help';
import EmailRecovery from '../pages/EmailRecovery';
import PrivacyPolices from '../pages/PrivacyPolices';

// --- Páginas públicas de módulos (lazy) ---
const OxeTechWorkInicial = React.lazy(() => import('../pages/OxeTechWorkInicial'));
const InovationMap = React.lazy(() => import('../pages/InovationMap'));
const OxeTechWork = React.lazy(() => import('../pages/OxeTechWork'));
// Modulo Work dentro do painel do aluno. As paginas antigas seguem publicas
// para visitante/empresa; aluno logado e redirecionado para o modulo.
const WorkModulo = React.lazy(() => import('../pages/WorkModulo'));
const OxeTechWorkInicialRota = redirecionaAluno(OxeTechWorkInicial, () => '/oxetech-work');
const OxeTechWorkRota = redirecionaAluno(OxeTechWork, () => '/oxetech-work?aba=vagas');
const OxeTechWorkVagaRota = redirecionaAluno(
  OxeTechWork,
  (p) => `/oxetech-work?aba=vagas&vaga=${encodeURIComponent(p?.match?.params?.id ?? '')}`
);
const Oxedin = React.lazy(() => import('../pages/Oxedin'));
const OxeTechLab = React.lazy(() => import('../pages/Oxetech_Lab'));
const DeclaracaoLab = React.lazy(() => import('../pages/DeclaracaoLab'));
const OxetechEdu = React.lazy(() => import('../OxeTechEdu/pages/Estudante/PaginaPrincipal/index'));
const OxetechAcademy = React.lazy(() => import('../pages/OxetechAcademy'));
const AcademyPsicologa = React.lazy(() => import('../pages/AcademyPsicologa'));
const AcademySocio = React.lazy(() => import('../pages/AcademySocio'));
const OxetechAcademyWork = React.lazy(() => import('../pages/OxetechAcademyWork'));
const AcademyWorkContratacoes = React.lazy(() => import('../pages/AcademyWorkContratacoes'));
const OxetechLabDivulgacao = React.lazy(() => import('../pages/Oxetech_lab_Divulgacao'));
const RoteiroTestesAcademy = React.lazy(() => import('../pages/RoteiroTestesAcademy'));
const ValidarCertificado = React.lazy(() => import('../pages/ValidarCertificado'));

// --- Módulo PIBIC Jr (lazy) ---
const PibicJrLanding = React.lazy(() => import('../PibicJr/pages/LandingPage'));
const PibicJrVinculacao = React.lazy(() => import('../PibicJr/pages/Vinculacao'));
const PibicJrSalaDeAula = React.lazy(() => import('../PibicJr/pages/SalaDeAula'));
const PibicJrForum = React.lazy(() => import('../PibicJr/pages/Forum'));
const PibicJrChat = React.lazy(() => import('../PibicJr/pages/Chat'));
const PibicJrMateriais = React.lazy(() => import('../PibicJr/pages/Materiais'));
const AdminPibicJr = React.lazy(() => import('../PibicJr/pages/AdminPibicJr'));
const DashboardCoordenador = React.lazy(() => import('../PibicJr/pages/DashboardCoordenador'));
const PerfilCoordenadorPibic = React.lazy(() => import('../PibicJr/pages/PerfilCoordenador'));

// --- Módulo Aluno (lazy) ---
const Dashboard = React.lazy(() => import('../pages/Dashboard'));
const EditProfileAluno = React.lazy(() => import('../pages/EditProfileAluno'));
const PageTrilhaNew = React.lazy(() => import('../pages/PageTrilhaNew'));
const PageTrilhaDeConhecimento = React.lazy(() => import('../components/PageTrilhaDeConhecimento'));
const PageCursoTrilha = React.lazy(() => import('../components/PageCursoTrilha'));
const TrilhaPlayer = React.lazy(() => import('../components/TrilhaPlayer'));
// Player do OxeTrilhas dentro do layout do painel (sidebar em modo aula).
const TrilhaAula = React.lazy(() => import('../pages/TrilhaAula'));
const OxeTechWorkInscricao = React.lazy(() => import('../pages/OxetechWorkInscricao'));

// --- Módulo Lab (lazy) ---
const DashLab = React.lazy(() => import('../pages/DashLab'));

// --- Módulo Edu (lazy) ---
const PerfilEscola = React.lazy(() => import('../OxeTechEdu/pages/Bolsista/PerfilPrincipal/index'));
const ValidarAlunos = React.lazy(() => import('../OxeTechEdu/pages/ValidarAlunos/index').then(m => ({ default: m.ValidarAlunos })));
const DashEscola = React.lazy(() => import('../OxeTechEdu/pages/Bolsista/DashboardTurma'));
const Acompanhamento = React.lazy(() => import('../OxeTechEdu/pages/Acompanhamento').then(m => ({ default: m.Acompanhamento })));

// --- Convite Academy (lazy) ---
const AcademyConvite = React.lazy(() => import('../pages/AcademyConvite'));

// --- Quiz NEON 2026 (rota pública oculta para o evento) ---
const QuizNeon2026 = React.lazy(() => import('../pages/QuizNeon2026'));

// --- Quiz OxeTech (rota pública /quiz — programação jr/pl/sr com ranking) ---
const QuizOxetech = React.lazy(() => import('../pages/QuizOxetech'));

// --- Módulo Academy (lazy) ---
const LiveClassPage = React.lazy(() => import('../pages/LiveClassPage'));
const AdminYouTubeConnect = React.lazy(() => import('../pages/AdminYouTubeConnect'));
const SalaDeAula = React.lazy(() => import('../pages/SalaDeAula'));
const MinhasTurmasInstrutor = React.lazy(() => import('../pages/MinhasTurmasInstrutor'));
const PainelInstrutor = React.lazy(() => import('../pages/PainelInstrutor'));
const InstrutorMinhasSolicitacoes = React.lazy(() => import('../pages/InstrutorMinhasSolicitacoes'));
const AcademyMuralIdeias = React.lazy(() => import('../pages/AcademyMuralIdeias'));

// --- Módulo Work (lazy) ---
const WorkRelatoriosBolsista = React.lazy(() => import('../pages/WorkRelatoriosBolsista'));

// --- Módulo Empresa (lazy) ---
const BancoTalentos = React.lazy(() => import('../pages/BancoTalentos'));
const PageVagaEmpresa = React.lazy(() => import('../pages/PageVagaEmpresa'));
const EditProfileEmpresa = React.lazy(() => import('../pages/EditProfileEmpresa'));
// OxeTech Work dentro do painel da empresa. "empresa-work" com hifen nao casa o
// regex de API do nginx (que pega /empresa/ e /oxetechwork), entao o F5 funciona.
const EmpresaWork = React.lazy(() => import('../pages/EmpresaWork'));
// Relatórios do OxeTech Work da empresa (um por bolsista). Mesmo cuidado do
// hífen: "empresa-relatorios" não casa o regex de API do nginx.
const EmpresaWorkRelatorios = React.lazy(() => import('../pages/EmpresaWorkRelatorios'));

// --- Módulo Admin (lazy) ---
const DashAdmin = React.lazy(() => import('../pages/DashAdmin'));
const PainelPesquisas = React.lazy(() => import('../pages/PainelPesquisas'));
const DashAdminWork = React.lazy(() => import('../pages/DashAdminWork'));
const GerenciamentoAdmin = React.lazy(() => import('../pages/GerenciamentoAdmin'));
const PageAlunos = React.lazy(() => import('../pages/PageAlunos'));
const DashOXL = React.lazy(() => import('../pages/DashOXL'));
const DashOXD = React.lazy(() => import('../OxeTechEdu/pages/DashOxd'));
const PageVagasAdm = React.lazy(() => import('../pages/PageVagasAdm'));
const ProfileAlunoAdm = React.lazy(() => import('../pages/ProfileAlunoAdm'));
const OxeTechWorkInscritos = React.lazy(() => import('../pages/OxetechWorkInscritos'));
const AdmCursos = React.lazy(() => import('../components/DashUserAdm/AdmCursos').then(m => ({ default: m.AdmCursos })));
const AdminAcademy = React.lazy(() => import('../pages/AdminAcademy'));
const AdminSuporte = React.lazy(() => import('../pages/AdminSuporte'));
const AdminMinicursos = React.lazy(() => import('../pages/AdminMinicursos'));
const PageVagaAdm = React.lazy(() => import('../pages/PageVagaAdm'));

// --- OxeTech HQ (gestão interna da equipe) ---
const HqDashboard = React.lazy(() => import('../pages/Hq/HqDashboard'));
const HqTarefas = React.lazy(() => import('../pages/Hq/HqTarefas'));
const HqBacklog = React.lazy(() => import('../pages/Hq/HqBacklog'));
const HqHandoff = React.lazy(() => import('../pages/Hq/HqHandoff'));
const HqKpis = React.lazy(() => import('../pages/Hq/HqKpis'));
const HqTracker = React.lazy(() => import('../pages/Hq/HqTracker'));
const HqPresenca = React.lazy(() => import('../pages/Hq/HqPresenca'));
const HqEquipe = React.lazy(() => import('../pages/Hq/HqEquipe'));
const HqPaginas = React.lazy(() => import('../pages/Hq/HqPaginas'));
const HqMapa = React.lazy(() => import('../pages/Hq/HqMapa'));
const HqAssistente = React.lazy(() => import('../pages/Hq/HqAssistente'));
const HqTokensApi = React.lazy(() => import('../pages/Hq/HqTokensApi'));
const HqAlertas = React.lazy(() => import('../pages/Hq/HqAlertas'));
const HqGestao = React.lazy(() => import('../pages/Hq/HqGestao'));
const HqTokensApiComoFunciona = React.lazy(() => import('../pages/Hq/HqTokensApi/ComoFuncionaMcp'));

// --- Fallback de loading ---
function LazyFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <CircularProgress sx={{ color: 'var(--color-primary)' }} />
    </Box>
  );
}

export default function Routes() {
  const location = useLocation();

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
    <PageTransition key={location.pathname}>
    <Suspense fallback={<LazyFallback />}>
    <Switch location={location}>
      {/* ROTAS PÚBLICAS */}
      <Route path="/" exact component={Main} />
      <Route path="/oxe-tech-work" exact component={OxeTechWorkInicialRota} />

      <Route path="/login" exact component={SignIn} />
      <Route path="/logout" exact component={SignOut} />
      <Route path="/sucess" component={Confirm} />
      <Route path="/forgot" component={ForgotPasword} />
      {/* Enderecos novos usados pela Central de ajuda; os antigos continuam. */}
      <Route path="/recuperar-senha" component={ForgotPasword} />
      <Route path="/change-password" component={ChangePassword} standalone />
      <Route path="/cadastro" component={SignUpAluno} />
      <Route path="/empresa-cadastro" component={SignUpEmpresa} />
      <Route path="/select-cadastro" component={SelectsignUp2} />
      <Route path="/politicas-privacidade" component={PrivacyPolices} />
      <Route path="/inovation-map" component={InovationMap} />
      <Route path="/oxedin" component={Oxedin} />
      {/* Link direto de uma vaga: renderiza a mesma página e o VagasOxetechWork abre
          o modal da vaga pelo id da URL. Vem ANTES de "/oxetechwork" porque o Switch
          para na primeira rota que casa. Precisa do location ^~ /oxetechwork/vaga/
          no nginx, senão o regex de API manda essa URL pro backend (401). */}
      <Route path="/oxetechwork/vaga/:id" component={OxeTechWorkVagaRota} />
      <Route path="/oxetechwork" component={OxeTechWorkRota} />
      <Route path="/oxetechlab" component={OxeTechLab}/>
      <Route path="/oxetechedu" component={OxetechEdu} />
      <Route path="/oxetechacademy" component={OxetechAcademy} />
      <AlunoRoute path="/academy-psicologa" component={AcademyPsicologa} />
      <AlunoRoute path="/academy-socio" component={AcademySocio} />
      {/* Antes de /academy-work: dentro do Switch, a primeira rota que casa vence. */}
        <AlunoRoute path="/academy-work-contratacoes" component={AcademyWorkContratacoes} />
        <AlunoRoute path="/academy-work" component={OxetechAcademyWork} />
      <AlunoRoute path="/academy-ideias" component={AcademyMuralIdeias} />
      <Route path="/oxetechlabdivulgacao" component={OxetechLabDivulgacao} />
      <Route path="/roteiro-testes-academy" component={RoteiroTestesAcademy} />
      <Route path="/academy/convite/:token" component={AcademyConvite} standalone />
      <Route path="/quiz-neon2026" component={QuizNeon2026} standalone />
      <Route path="/quiz" component={QuizOxetech} standalone />
      {/* Conferência pública de certificado (aluno e instrutor). A rota com o
          código vem antes da sem código: dentro do Switch, a primeira que casa
          vence. O caminho evita os prefixos "certificados" e "validation", que
          o regex de API do nginx entrega ao Express. */}
      <Route path="/validar-certificado/:codigo" component={ValidarCertificado} standalone />
      <Route path="/validar-certificado" component={ValidarCertificado} standalone />
      <Route path="/pibic-jr" exact component={PibicJrLanding} />

      {/* ROTAS PARA USUÁRIO PESSOA FÍSICA */}
        <AlunoRoute path="/oxetrilhas" component={PageTrilhaNew}/>
        <AlunoRoute path="/dashboard" component={Dashboard} />
        <AlunoRoute path="/account" component={EditProfileAluno}/>
        <AlunoRoute
          path="/oxetechwork-inscricao"
          component={OxeTechWorkInscricao}
        />
        <AlunoRoute path="/work-relatorios" component={WorkRelatoriosBolsista}/>
        {/* "oxetech-work" com hifen: nao casa o regex de API do nginx (que pega
            /oxetechwork e /work) e por isso funciona no F5. */}
        <AlunoRoute path="/oxetech-work/inscricao/:editalId" component={WorkModulo}/>
        <AlunoRoute path="/oxetech-work" exact component={WorkModulo}/>

        {/* OxetechLab */}
          <AlunoRoute path="/perfil-laboratorio/:labId" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/turma-selecao-painel/:id" component={DashLab} isLab />
          <AlunoRoute path="/lab/:labId/turma-iniciada-painel/:id" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/painel-instrutor/:turmaId" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/calendario" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/catalogo-cursos" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/sugerir-melhoria" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/oxetech-academy" component={DashLab} isLab/>
          <AlunoRoute path="/lab/:labId/diarios" component={DashLab} isLab/>

        {/* Routes Trilha de Conhecimento */}
          <AlunoRoute path="/trilha/:idTrilha/curso/:idCurso" component={PageCursoTrilha} />
          {/* Player novo (estilo Cisco) é a rota oficial; -v2 mantido como alias
              e -old preserva a tela antiga para comparação */}
          <AlunoRoute path="/oxetrilha-v2/:id" component={TrilhaPlayer} standalone />
          <AlunoRoute path="/oxetrilha-old/:id" component={PageTrilhaDeConhecimento} />
          {/* Endereco antigo do player: vai para o novo, dentro do painel. */}
          <RotaPura path="/oxetrilha/:id" render={({ match }) => <Redirect to={`/trilhas/${match.params.id}`} />} />
          <AlunoRoute path="/trilhas/:id/aula/:aulaKey" component={TrilhaAula} />
          <AlunoRoute path="/trilhas/:id" exact component={TrilhaAula} />

        {/* Routes OxetechEdu */}
          <AlunoRoute path="/escola/:escolaId" component={PerfilEscola} isEdu/>
          <AlunoRoute path="/validar/:escolaId" component={ValidarAlunos} isEdu/>
          <AlunoRoute path="/dash/:escolaId/turma/:turmaId" component={DashEscola} isEdu />
          <AlunoRoute path="/acompanhamento/:escolaId" component={Acompanhamento} isEdu />
          
          <AlunoRoute path="/declaracao-lab" component={DeclaracaoLab} />
          {/* Mesmo conteudo num caminho que o regex de API do nginx de prod nao
              captura (/declaracao-lab vai para a API no F5/link direto). */}
          <AlunoRoute path="/minha-declaracao" component={DeclaracaoLab} />

          {/* PIBIC Jr — Aluno */}
          <AlunoRoute path="/pibic-jr/vinculacao" component={PibicJrVinculacao} />
          <AlunoRoute path="/pibic-jr/sala-de-aula" component={PibicJrSalaDeAula} />
          <AlunoRoute path="/pibic-jr/forum" component={PibicJrForum} />
          <AlunoRoute path="/pibic-jr/chat" component={PibicJrChat} />
          <AlunoRoute path="/pibic-jr/materiais" component={PibicJrMateriais} />
          <AlunoRoute path="/pibic-jr/coordenador" component={PerfilCoordenadorPibic} />

          {/* Sala de Aula Digital */}
          <AlunoRoute path="/live-class/:turmaId" component={LiveClassPage} />
          <AlunoRoute path="/sala-de-aula/:turmaId" component={SalaDeAula} />
          <AlunoRoute path="/minhas-turmas-instrutor" component={MinhasTurmasInstrutor} />
          <AlunoRoute path="/painel-instrutor/:turmaId" component={PainelInstrutor} />
          <AlunoRoute path="/instrutor/minhas-solicitacoes" component={InstrutorMinhasSolicitacoes} />

      {/* ROTAS PARA USUÁRIO PESSOA JURÍDICA */}
        <EmpresaRoute path="/bancotalentos" component={BancoTalentos} isWork/>
        <EmpresaRoute path="/minha-vaga/:id" component={PageVagaEmpresa} />
        <EmpresaRoute path="/empresa-account" component={EditProfileEmpresa}/>
        <EmpresaRoute path="/empresa-work" component={EmpresaWork}/>
        <EmpresaRoute path="/empresa-relatorios" component={EmpresaWorkRelatorios}/>
      
      {/* ROTAS PARA USUÁRIO ADMIN */}
        <AdminRoute path="/dashadmin" exact component={DashAdmin}/>
        <AdminRoute path="/painel-pesquisas" component={PainelPesquisas}/>
        <AdminRoute path="/dashadmin/work" component={DashAdminWork}/>
        <AdminRoute path="/admin/youtube-connect" component={AdminYouTubeConnect}/>
        <AdminRoute path="/admin-certificados" component={AdminCertificadosLote}/>
        <AdminRoute path="/administradores" component={GerenciamentoAdmin} isSudo />
        <AdminRoute path="/alunos" component={PageAlunos}  />
        <AdminRoute path="/oxetechlab-dashboard" component={DashOXL} />
        <AdminRoute path="/oxetechedu-dashboard" component={DashOXD} />      
        <AdminRoute path="/vagas" component={PageVagasAdm}  />
        <AdminRoute path="/aluno/:id" component={ProfileAlunoAdm} />
        <AdminRoute
          path="/oxetechwork-inscritos"
          component={OxeTechWorkInscritos}
        />
        <AdminRoute path="/trilhas-admin" component={AdmCursos} />
        <AdminRoute path="/admin-academy" component={AdminAcademy} />
        <AdminRoute path="/admin-suporte" component={AdminSuporte} />
        <AdminRoute path="/admin-minicursos" component={AdminMinicursos} />
        <AdminRoute path="/showvaga/:id" component={PageVagaAdm} />
        <AdminRoute path="/admin-pibicjr" component={AdminPibicJr} />
        <AdminRoute path="/dashboard-pibicjr" component={DashboardCoordenador} />

      <Route path="/ajuda" component={Help} />
      <Route path="/recuperar-acesso" component={EmailRecovery} />
      <Route path="/alterar-email" component={EmailRecovery} />
      {/* OxeTech HQ — gestão interna (usa JWT principal do OxeTech) */}
      {/* Painel da direcao — o backend recusa quem nao for hq_nivel "direcao" */}
      <HqRoute path="/hq/alertas" component={HqAlertas} />
      <HqRoute path="/hq/gestao" component={HqGestao} />
      <HqRoute path="/hq/tarefas" component={HqTarefas} />
      <HqRoute path="/hq/backlog" component={HqBacklog} />
      <HqRoute path="/hq/handoffs" component={HqHandoff} />
      <HqRoute path="/hq/kpis" component={HqKpis} />
      <HqRoute path="/hq/tracker" component={HqTracker} />
      <HqRoute path="/hq/presenca" component={HqPresenca} />
      <HqRoute path="/hq/equipe" component={HqEquipe} />
      <HqRoute path="/hq/mapa" component={HqMapa} />
      <HqRoute path="/hq/assistente" component={HqAssistente} />
      <HqRoute path="/hq/paginas" component={HqPaginas} />
      <HqRoute path="/hq/tokens-api/como-funciona" component={HqTokensApiComoFunciona} />
      <HqRoute path="/hq/tokens-api" component={HqTokensApi} />
      <HqRoute exact path="/hq" component={HqDashboard} />

      <Route path="/" component={Error} />
    </Switch>
    </Suspense>
    </PageTransition>
    </AnimatePresence>
  );
}